import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Edit2, Check, Copy, Fan } from 'lucide-react';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig, IconStyleId } from '../../types';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

/**
 * Fan Template 01 - Modern UI
 * 
 * 風扇專用模板，與 HVAC 不同：
 * - 沒有溫度設定
 * - 只有電源開關和風速控制
 * 
 * 使用的模組化組件：
 * - OnOff/Style05_TextButton (電源開關文字按鈕)
 * - FanSpeed/Style02_Pills (風速設定藥丸按鈕)
 */
export const Template01_Modern: React.FC<DeviceTemplateProps> = ({
  device,
  isExecutingCommand,
  onTraitControl,
  onClose,
  onNicknameSave,
  config,
  isMerged,
  endpointCount,
  onToggleMerge
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const traitOrder: string[] = config?.traitOrder || ['OnOff', 'FanSpeed', 'Mode', 'Timer'];
  const hiddenComponents: string[] = config?.hiddenComponents || [];

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };
  
  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };

  const attributes = device.attributes || {};
  
  // Extract fan speeds from API response
  // Primary source: availableCustomModes (contains FanSpeed settings)
  // Secondary source: availableFanSpeeds.speeds (Google Home format)
  // device.fanSpeed stores the current value (numeric '0','1','2','3')
  // MCP setFanSpeed expects speed name ('auto', 'low', 'medium', 'high')
  const { fanSpeeds, fanSpeedLabels, numericToName } = (() => {
    // Priority 1: Check availableCustomModes for FanSpeed settings (most reliable)
    const customModes = attributes.availableCustomModes || [];
    const fanSpeedMode = customModes.find((m: any) => m.name === 'FanSpeed');
    
    // Priority 2: Check availableFanSpeeds.speeds (Google Home format)
    const googleSpeeds = (attributes.availableFanSpeeds as any)?.speeds;
    
    // Build speed name mapping from Google Home format if available
    const speedNameMap: Record<string, string> = {};
    if (Array.isArray(googleSpeeds)) {
      googleSpeeds.forEach((s: any) => {
        const speedName = s.speed_name; // 'auto', 'low', 'medium', 'high'
        const synonyms = s.speed_values?.[0]?.speed_synonym || [];
        synonyms.forEach((syn: string) => {
          speedNameMap[syn] = speedName;
        });
      });
    }
    
    // Get speed options (numeric values like ['0', '1', '2', '3'])
    const speeds: string[] = fanSpeedMode?.settings || ['0', '1', '2', '3'];
    
    // Build labels and mappings based on speeds.length
    const labels: Record<string, string> = {};
    const numToName: Record<string, string> = {};
    
    // Map speed values to Chinese labels
    // Use speeds.length to determine mapping:
    // 3-speed fans: 0=低速, 1=中速, 2=高速
    // 4-speed fans: 0=自動, 1=低速, 2=中速, 3=高速
    speeds.forEach((speed: string) => {
      // Check if we have Google Home mapping first
      const apiName = speedNameMap[speed];
      if (apiName) {
        numToName[speed] = apiName;
        switch (apiName.toLowerCase()) {
          case 'auto': labels[speed] = '自動'; break;
          case 'low': labels[speed] = '低速'; break;
          case 'medium': case 'med': labels[speed] = '中速'; break;
          case 'high': labels[speed] = '高速'; break;
          default: labels[speed] = apiName;
        }
      } else {
        // Fallback based on speeds.length
        if (speeds.length === 3) {
          switch (speed) {
            case '0': labels[speed] = '低速'; numToName[speed] = 'low'; break;
            case '1': labels[speed] = '中速'; numToName[speed] = 'medium'; break;
            case '2': labels[speed] = '高速'; numToName[speed] = 'high'; break;
            default: labels[speed] = `速度 ${speed}`; numToName[speed] = speed;
          }
        } else {
          // 4-speed (default)
          switch (speed) {
            case '0': labels[speed] = '自動'; numToName[speed] = 'auto'; break;
            case '1': labels[speed] = '低速'; numToName[speed] = 'low'; break;
            case '2': labels[speed] = '中速'; numToName[speed] = 'medium'; break;
            case '3': labels[speed] = '高速'; numToName[speed] = 'high'; break;
            default: labels[speed] = `速度 ${speed}`; numToName[speed] = speed;
          }
        }
      }
    });
    
    return { fanSpeeds: speeds, fanSpeedLabels: labels, numericToName: numToName };
  })();
  
  // Device state
  const isDeviceOn = device.isOn ?? false;
  const isDeviceOff = !isDeviceOn;
  // Convert fanSpeed to string for comparison (API may return number or string)
  const currentFanSpeed = String(device.fanSpeed ?? '0');

  // Calculate animation duration based on fan speed
  // Faster speed = shorter animation duration (faster rotation)
  const getSpinDuration = (): string => {
    const speedName = numericToName[currentFanSpeed] || currentFanSpeed;
    switch (speedName.toLowerCase()) {
      case 'high':
      case '3':
        return '0.5s';  // Fastest
      case 'medium':
      case 'med':
      case '2':
        return '1s';    // Medium
      case 'low':
      case '1':
        return '2s';    // Slow
      case 'auto':
      case '0':
      default:
        return '1.5s';  // Auto (moderate speed)
    }
  };
  
  const spinDuration = getSpinDuration();

  // Handlers
  const handlePowerToggle = () => {
    onTraitControl(device, 'OnOff', isDeviceOn ? 'off' : 'on');
  };

  const handleFanSpeedChange = (speed: string) => {
    // Convert numeric value to speed name for MCP API
    // MCP setFanSpeed expects: 'auto', 'low', 'medium', 'high'
    const speedName = numericToName[speed] || speed;
    onTraitControl(device, 'FanSpeed', 'set', { 
      fanSpeed: speedName 
    });
  };

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="fan" 
              isOn={isDeviceOn} 
              size={20}
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{device.nickname || device.deviceName}</h2>
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      setTempName(device.nickname || device.deviceName);
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.status === 'online',
                label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          
          {/* Big Icon - Fan Visual using DeviceIcon */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-6">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDeviceOn 
                  ? 'bg-gradient-to-br from-cyan-500/30 to-teal-500/30 border-2 border-cyan-400/50 shadow-lg shadow-cyan-500/30' 
                  : 'bg-slate-800/50 border-2 border-slate-700'
              }`}>
                <DeviceIcon
                  deviceType="fan"
                  isOn={isDeviceOn}
                  size={64}
                  styleId={bigIconStyle}
                  className={isDeviceOn ? 'text-cyan-400' : 'text-slate-500'}
                />
              </div>
            </div>
          )}

          {/* Dynamically rendered traits based on order */}
          {traitOrder.map((traitName) => {
            // Check if component is hidden
            if (hiddenComponents.includes(traitName)) {
              return null;
            }
            
            switch (traitName) {
              case 'OnOff':
                return (
                  <div key="OnOff" className="py-2 border-b border-slate-700/30">
                    <DynamicTrait
                      traitName="OnOff"
                      styleId={getTraitStyleId('OnOff', traitStyles)}
                      componentProps={{
                        isOn: isDeviceOn,
                        onToggle: handlePowerToggle,
                        disabled: isExecutingCommand,
                        loading: isExecutingCommand,
                        label: "電源"
                      }}
                    />
                  </div>
                );
              case 'FanSpeed':
                return (
                  <div key="FanSpeed" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="FanSpeed"
                      styleId={getTraitStyleId('FanSpeed', traitStyles)}
                      componentProps={{
                        value: currentFanSpeed,
                        options: fanSpeeds,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: handleFanSpeedChange,
                        labels: fanSpeedLabels
                      }}
                    />
                  </div>
                );
              case 'Mode':
                // Get available modes from device attributes
                const availableModes = (attributes.availableModes as any[]) || [];
                const currentMode = (attributes as any).currentMode || (attributes as any).mode;
                if (availableModes.length === 0) return null;
                return (
                  <div key="Mode" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="Mode"
                      styleId={getTraitStyleId('Mode', traitStyles)}
                      componentProps={{
                        value: currentMode,
                        options: availableModes.map((m: any) => m.name || m),
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: (mode: string) => onTraitControl(device, 'Mode', 'set', { mode }),
                        label: '模式'
                      }}
                    />
                  </div>
                );
              case 'Timer':
                const timerValue = (attributes as any).timerRemaining || (attributes as any).timer || 0;
                return (
                  <div key="Timer" className={`transition-opacity ${isDeviceOff ? 'opacity-40 pointer-events-none' : ''}`}>
                    <DynamicTrait
                      traitName="Timer"
                      styleId={getTraitStyleId('Timer', traitStyles)}
                      componentProps={{
                        value: timerValue,
                        disabled: isExecutingCommand || isDeviceOff,
                        loading: isExecutingCommand,
                        onChange: (minutes: number) => onTraitControl(device, 'Timer', 'set', { timerMinutes: minutes }),
                        label: '計時器'
                      }}
                    />
                  </div>
                );
              default:
                return null;
            }
          })}

        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle - only for multi_channel devices */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{device.deviceId || device.baseSN}</span>
              <button
                onClick={() => navigator.clipboard.writeText(device.deviceId || device.baseSN)}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Modern;
